import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useNineBoxProcesses, useNineBoxProcessGrid } from './config';
import ProcessStartDrawer from './ProcessStartDrawer';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import Avatar from '../../components/ui/Avatar';
import Tooltip from '../../components/ui/Tooltip';
import api from '../../services/api';

// Task 34 — Faz A'nın döngü/departman filtreli, taşıma+manuel-giriş modallı sayfası süreç-
// bazlı akışa evrildi: üstte süreç seçici + "Yeni Süreç" drawer'ı, matris SEÇİLİ sürecin
// placement'larını (YENİ cellKey seti + strateji tooltip'i) çizer. Taşı/Manuel Giriş
// KALDIRILDI — süreç-bazlı grid'te hücre override mekanizması yok (bkz. NineBoxDataAccess
// Task 20 yorumu); X ekseni artık ya "auto" (dönem) ya "form" (ManagerRatingPage) üzerinden
// gelir.

// Layout'un Main'i zaten 16px 16px padding veriyor (bkz. components/layout/Layout.js) —
// sayfa kendi ekstra padding'ini eklemez (kanonik Container = styled.div`` deseni).
const Page = styled.div``;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 12px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const ToolbarRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 20px;
`;

const FilterItem = styled.div`
  width: 240px;
`;

// Task 34 fix-turu (incelemeci Important #1) — 41de759 emsali (TalentDashboardPage'in
// ${$accent}1A arka-plan + tam accent metin/renk deseni): açık/kapalı rozetleri artık
// auxiliar.warning/success token'larından türetiliyor, hardcode hex YOK.
const statusAccent = (theme, open) => (open ? theme.colors.auxiliar.warning : theme.colors.auxiliar.success);

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  margin-left: 10px;
  background: ${(p) => statusAccent(p.theme, p.$open)}1A;
  color: ${(p) => statusAccent(p.theme, p.$open)};
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 40px repeat(3, 1fr);
  grid-template-rows: 40px repeat(3, auto);
  gap: 0;
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 8px;
  overflow: hidden;
`;

const CornerCell = styled.div`
  background: ${({ theme }) => theme.colors.bgColor};
  border-right: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
`;

const AxisLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.bgColor};
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
  padding: 6px 4px;
  text-align: center;
`;

const GridAxisLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.bgColor};
  border-right: 1px solid ${({ theme }) => theme.colors.surface.border};
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  padding: 8px 4px;
`;

// Task 34 — YENİ cellKey token'ları (NineBoxCellKeys.Map ile birebir): satır = potansiyel
// bandı (high/mid/low, üstten alta), sütun = performans bandı (low/mid/high, soldan sağa).
const GRID_LAYOUT = [
  ['hidden_talent', 'promising', 'star_hipo'],
  ['developing', 'core_employee', 'high_performer'],
  ['attention_needed', 'reliable_performer', 'expert_master'],
];

const POT_LEVELS = ['high', 'mid', 'low'];
const PERF_LEVELS = ['low', 'mid', 'high'];

// Eski Faz A renk paletinin (high_high yeşil ... low_low kırmızı) YENİ token'lara birebir
// aktarımı — grid pozisyonu değişmedi, yalnızca anahtar adları değişti. Task 34 fix-turu
// (incelemeci Important #1) bu 9 pastel hücre rengini BİLİNÇLİ olarak temaya taşımadı —
// tema paletinde 9 ayrı ısı-haritası tonuna karşılık gelen token yok, tek bir sayfaya özel
// 9 yeni token eklemek küçük çaplı bir görev için orantısız; incelemeci de bunu "isteğe
// bağlı" olarak işaretledi. Eski borç olarak kalıyor (bkz. task-34-report.md Fix raporu).
const CELL_COLORS = {
  star_hipo: '#C8E6C9',
  promising: '#DCEDC8',
  hidden_talent: '#FFF9C4',
  high_performer: '#B3E5FC',
  core_employee: '#FFF3E0',
  developing: '#FFCCBC',
  expert_master: '#E3F2FD',
  reliable_performer: '#FFCDD2',
  attention_needed: '#FFCDD2',
};

const GridCell = styled.div`
  background: ${(p) => CELL_COLORS[p.$cellKey] || p.theme.colors.bgColor};
  border-right: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
  padding: 10px;
  min-height: 120px;
  vertical-align: top;
  &:nth-child(3n+1) { border-right: none; }
`;

const CellHeader = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #444;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: help;
`;

const EmployeeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: rgba(255,255,255,0.75);
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: box-shadow 0.15s;
  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    background: #fff;
  }
`;

const EmployeeName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`;

const ScorePill = styled.span`
  font-size: 10px;
  color: #666;
  white-space: nowrap;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 440px;
  padding: 28px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
`;

const ModalTitle = styled.h3`
  margin: 0 0 20px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 13px;
  gap: 12px;
`;

const InfoLabel = styled.span`
  color: #888;
`;

const InfoValue = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: right;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const StateMsg = styled.div`
  padding: 48px;
  text-align: center;
  color: #888;
  font-size: 14px;
`;

const StrategyHint = styled.div`
  margin-top: 6px;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  line-height: 1.4;
`;

function buildDeptAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'name[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/departments', { params });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((d) => ({ id: d.id, name: d.name }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/departments/${id}`);
      const d = res.data?.data;
      return d ? { id: d.id, name: d.name } : null;
    },
  };
}

const deptAsync = buildDeptAsync();

export default function NineBoxPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('TD');

  const {
    processes, fetching: processesFetching, loadFailed: processesLoadFailed,
    startProcess, completeProcess,
  } = useNineBoxProcesses();
  const [selectedProcessId, setSelectedProcessId] = useState(null);
  const [filterDeptId, setFilterDeptId] = useState(null);
  const [showStartDrawer, setShowStartDrawer] = useState(false);
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false);
  // TD-13 — kısmi kapanış: doldurulmamış puanlamalar varken turu yine de kapatır.
  const [forceComplete, setForceComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);

  useEffect(() => {
    if (!selectedProcessId && processes.length > 0) setSelectedProcessId(processes[0].id);
  }, [processes, selectedProcessId]);

  const gridFilters = useMemo(() => ({ departmentId: filterDeptId || undefined }), [filterDeptId]);
  const {
    fetching: gridFetching, gridData, loadFailed: gridLoadFailed, scopeUnresolved,
    refresh: refreshGrid,
  } = useNineBoxProcessGrid(selectedProcessId, gridFilters);

  const selectedProcess = useMemo(
    () => processes.find((p) => p.id === selectedProcessId) || null,
    [processes, selectedProcessId],
  );

  const processOptions = useMemo(() => processes.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.ninebox.process.status.${p.status}`, { defaultValue: p.status })})`,
  })), [processes, t]);

  const cellMap = useMemo(() => {
    const map = {};
    (gridData || []).forEach((cell) => { map[cell.cellKey] = cell.employees || []; });
    return map;
  }, [gridData]);

  const handleStartSave = useCallback(async (payload) => {
    const ok = await startProcess(payload);
    return ok;
  }, [startProcess]);

  const handleCompleteConfirm = useCallback(async () => {
    if (!selectedProcessId) return;
    setCompleting(true);
    const ok = await completeProcess(selectedProcessId, forceComplete);
    if (ok) {
      setShowCompleteConfirm(false);
      setForceComplete(false);
      refreshGrid();
    }
    setCompleting(false);
  }, [selectedProcessId, completeProcess, forceComplete, refreshGrid]);

  const handleCardClick = useCallback((emp) => setSelectedEmp(emp), []);
  const handleCloseDetail = useCallback(() => setSelectedEmp(null), []);

  return (
    <Page>
      <PageHeader>
        <PageTitle>
          {t('performance.ninebox.title')}
          {selectedProcess && (
            <StatusBadge $open={selectedProcess.status === 'open'}>
              {t(`performance.ninebox.process.status.${selectedProcess.status}`, { defaultValue: selectedProcess.status })}
            </StatusBadge>
          )}
        </PageTitle>
        {canManage && (
          <Button onClick={() => setShowStartDrawer(true)}>
            {t('performance.ninebox.process.new')}
          </Button>
        )}
      </PageHeader>

      <ToolbarRow>
        <FilterItem>
          <GenericSelect
            label={t('performance.ninebox.process.select')}
            value={selectedProcessId}
            onChange={setSelectedProcessId}
            options={processOptions}
          />
        </FilterItem>
        <FilterItem>
          <AsyncGenericSelect
            label={t('performance.ninebox.department')}
            value={filterDeptId}
            onChange={setFilterDeptId}
            onSearch={deptAsync.onSearch}
            onGetById={deptAsync.onGetById}
          />
        </FilterItem>
        {canManage && selectedProcess?.status === 'open' && (
          <>
            <Button variant="outline" onClick={() => { setForceComplete(false); setShowCompleteConfirm(true); }}>
              {t('performance.ninebox.process.complete')}
            </Button>
            {/* TD-13 — tek bir yönetici formu doldurmazsa tur ESKİDEN hiç kapanmıyordu;
                dinamik yetenek havuzları da o yüzden yenilenmiyordu. Kısmi kapanış
                doldurulmuş puanlarla ilerler, eksikleri "atlandı" damgalar. */}
            <Button variant="outline" onClick={() => { setForceComplete(true); setShowCompleteConfirm(true); }}>
              {t('performance.ninebox.process.forceComplete')}
            </Button>
          </>
        )}
      </ToolbarRow>

      {/* TD-23 — süreç listesi yüklenemediyse "hiç süreç yok" DEMEYİZ. */}
      {processesLoadFailed && <LoadErrorBanner />}

      {/* TD-12 — kapsam hiç çözülemedi (çağıranın aktif pozisyon satırı yok):
          boş matris "ekibimde kimse değerlendirilmemiş" anlamına GELMEZ. */}
      {scopeUnresolved && <LoadErrorBanner message={t('performance.ninebox.scopeUnresolved')} />}

      {gridLoadFailed && <LoadErrorBanner />}

      {!processesFetching && !processesLoadFailed && processes.length === 0 && (
        <StateMsg>{t('performance.ninebox.process.empty')}</StateMsg>
      )}

      {processes.length > 0 && !selectedProcessId && (
        <StateMsg>{t('performance.ninebox.process.selectPrompt')}</StateMsg>
      )}

      {selectedProcessId && gridFetching && (
        <StateMsg>{t('common.loading')}</StateMsg>
      )}

      {selectedProcessId && !gridFetching && (
        <GridWrapper>
          <CornerCell />
          {PERF_LEVELS.map((perf) => (
            <AxisLabel key={perf}>{t('performance.ninebox.performance')} — {t(`performance.ninebox.${perf}`)}</AxisLabel>
          ))}

          {GRID_LAYOUT.map((row, rowIdx) => (
            [
              <GridAxisLabel key={`y-${rowIdx}`}>
                {t('performance.ninebox.potential')} {t(`performance.ninebox.${POT_LEVELS[rowIdx]}`)}
              </GridAxisLabel>,
              ...row.map((cellKey) => {
                const employees = cellMap[cellKey] || [];
                return (
                  <GridCell key={cellKey} $cellKey={cellKey}>
                    <Tooltip text={t(`performance.ninebox.strategy.${cellKey}`)}>
                      <CellHeader>{t(`performance.ninebox.cell.${cellKey}`)}</CellHeader>
                    </Tooltip>
                    {employees.map((emp) => (
                      <EmployeeCard key={emp.employeeId} onClick={() => handleCardClick(emp)}>
                        <Avatar
                          src={emp.employeeProfilePicture || undefined}
                          firstName={(emp.employeeName || '').split(' ')[0] || '?'}
                          lastName={(emp.employeeName || '').split(' ').slice(1).join(' ') || ''}
                          size={28}
                        />
                        <EmployeeName>{emp.employeeName}</EmployeeName>
                        <ScorePill>
                          {t('performance.ninebox.perfShort')}: {emp.performanceScore != null ? emp.performanceScore.toFixed(1) : '-'}
                          {' / '}
                          {t('performance.ninebox.potShort')}: {emp.potentialScore != null ? emp.potentialScore.toFixed(1) : '-'}
                        </ScorePill>
                      </EmployeeCard>
                    ))}
                  </GridCell>
                );
              }),
            ]
          ))}
        </GridWrapper>
      )}

      <ProcessStartDrawer
        open={showStartDrawer}
        onClose={() => setShowStartDrawer(false)}
        onSave={handleStartSave}
      />

      <ConfirmDialog
        isOpen={showCompleteConfirm}
        title={t('performance.ninebox.process.completeConfirmTitle')}
        message={forceComplete
          ? t('performance.ninebox.process.forceCompleteConfirmMessage')
          : t('performance.ninebox.process.completeConfirmMessage')}
        onConfirm={handleCompleteConfirm}
        onCancel={() => { setShowCompleteConfirm(false); setForceComplete(false); }}
        loading={completing}
      />

      {/* Çalışan detayı — salt okunur (süreç-bazlı grid'te hücre override yolu yok). */}
      <Overlay $open={!!selectedEmp}>
        {selectedEmp && (
          <ModalCard>
            <ModalTitle>{selectedEmp.employeeName}</ModalTitle>
            <InfoRow>
              <InfoLabel>{t('performance.ninebox.performance')}</InfoLabel>
              <InfoValue>{selectedEmp.performanceScore?.toFixed(2) ?? '-'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('performance.ninebox.potential')}</InfoLabel>
              <InfoValue>{selectedEmp.potentialScore?.toFixed(2) ?? '-'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>{t('performance.ninebox.cell.label')}</InfoLabel>
              <InfoValue>{t(`performance.ninebox.cell.${selectedEmp.cellKey}`)}</InfoValue>
            </InfoRow>
            {selectedEmp.performanceSource && (
              <InfoRow>
                <InfoLabel>{t('performance.ninebox.manualSource')}</InfoLabel>
                <InfoValue>
                  {t(`performance.ninebox.manualSource${selectedEmp.performanceSource === 'manual' ? 'Manual' : 'Cycle'}`)}
                </InfoValue>
              </InfoRow>
            )}
            {selectedEmp.cellKey && (
              <StrategyHint>{t(`performance.ninebox.strategy.${selectedEmp.cellKey}`)}</StrategyHint>
            )}
            <Actions>
              <Button variant="outline" onClick={handleCloseDetail}>{t('common.close')}</Button>
            </Actions>
          </ModalCard>
        )}
      </Overlay>
    </Page>
  );
}
